import type { DeferredEntityType } from '@/store/table-editing.store'
import type {
  CacheInvalidationStrategy,
  CreateMutationResult,
  DeleteMutationResult,
  QueryKeyFactory,
  UpdateMutationResult,
} from '@/types/query.types'
import { createLogger } from '@/lib/logger'
import { postMessage } from '@/lib/realtime/broadcast'
/**
 * Generic resource mutation factory
 * Creates standardized mutation hooks for any resource type
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const logger = createLogger('hooks/mutations/createResourceMutations')

/**
 * Configuration for resource mutations
 */
export interface ResourceMutationConfig<T, TCreate, TUpdate> {
  /** Resource name for user-facing messages */
  resourceName: string
  /** Singular resource name (e.g., 'project', 'task') */
  resourceNameSingular: string
  /** Lazily resolves user-facing labels (e.g., org-configured terminology) */
  getLabels?: () => { singular: string; Singular: string }
  /** Query key factory for cache invalidation */
  queryKeyFactory: QueryKeyFactory

  /** API client with mutation methods */
  api: {
    create: (data: TCreate) => Promise<T>
    update: (id: string, data: TUpdate) => Promise<T>
    updateField?: (id: string, field: string, value: unknown) => Promise<T>
    delete: (id: string) => Promise<void>
    bulkDelete?: (ids: string[]) => Promise<void>
  }

  /** Custom cache invalidation strategies */
  invalidation?: {
    onCreate?: CacheInvalidationStrategy
    onUpdate?: CacheInvalidationStrategy
    onDelete?: CacheInvalidationStrategy
  }

  /** Enable realtime broadcast for mutations */
  enableRealtime?: boolean

  /** Full reader-projected responses replace rows so omitted fields cannot survive. */
  replaceUpdatedRows?: boolean

  /**
   * Property holding the row array in a list cache entry, for resources whose
   * API returns a named envelope instead of `{ data, meta }` (e.g. suppliers
   * return `{ suppliers, total, page, limit }`). Without this, optimistic list
   * patches silently no-op and edited cells keep showing the old value until
   * something else refetches. Bare arrays are always handled.
   */
  listItemsKey?: string

  /** Entity type for deferred updates (when popovers are open) */
  deferredEntityType?: DeferredEntityType

  /** Custom success messages */
  messages?: {
    createSuccess?: string
    updateSuccess?: string
    deleteSuccess?: string
    createError?: string
    updateError?: string
    deleteError?: string
  }
}

/**
 * Resolves the labels used in user-facing toast messages, preferring the
 * lazily-evaluated `getLabels` (org-configured terminology) when provided.
 */
function resolveLabels(config: ResourceMutationConfig<any, any, any>): {
  singular: string
  Singular: string
} {
  if (config.getLabels) {
    return config.getLabels()
  }
  return {
    singular: config.resourceNameSingular,
    Singular: config.resourceName,
  }
}

/**
 * Applies `patch` to the row matching `id` inside a list cache entry, whatever
 * envelope that entry uses: a bare array, the standard `{ data: [...] }`, or a
 * resource-specific key declared via `listItemsKey`. Returns `oldData`
 * untouched when the shape isn't recognised, so an unknown envelope degrades to
 * "no optimistic update" rather than a corrupted cache.
 */
export function patchRowInListCache(
  oldData: any,
  id: string,
  patch: (item: any) => any,
  listItemsKey?: string,
): any {
  if (!oldData) return oldData

  const patchArray = (rows: any[]) =>
    rows.map((item: any) => (item.id === id ? patch(item) : item))

  if (Array.isArray(oldData)) {
    return patchArray(oldData)
  }

  if (typeof oldData === 'object') {
    for (const key of ['data', listItemsKey]) {
      if (key && Array.isArray(oldData[key])) {
        return { ...oldData, [key]: patchArray(oldData[key]) }
      }
    }
  }

  return oldData
}

/**
 * Creates a create mutation hook
 */
export function createCreateMutation<T, TCreate>(
  config: ResourceMutationConfig<T, TCreate, any>,
): () => CreateMutationResult<T, { data: TCreate }> {
  return function useCreateMutation() {
    const queryClient = useQueryClient()

    return useMutation({
      mutationFn: ({ data }: { data: TCreate }) => config.api.create(data),
      onSuccess: newItem => {
        // Invalidate lists
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
        })

        // Set the new item in cache
        const itemWithId = newItem as T & { id: string }
        if (itemWithId.id) {
          queryClient.setQueryData(
            config.queryKeyFactory.detail(itemWithId.id, {
              includeCustomFields: true,
            }),
            newItem,
          )
        }

        // Custom invalidation strategy
        if (config.invalidation?.onCreate) {
          applyInvalidationStrategy(queryClient, config.invalidation.onCreate)
        }

        // Realtime broadcast
        if (config.enableRealtime && itemWithId.id) {
          postMessage(`${config.resourceNameSingular}:created`, {
            id: itemWithId.id,
            [config.resourceNameSingular]: newItem,
            ts: Date.now(),
          })
        }
      },
      onError: error => {
        logger.error(`[useCreate${config.resourceName}Mutation] Error:`, error)
        const errorMessage =
          config.messages?.createError ||
          (error instanceof Error
            ? error.message
            : `Failed to create ${resolveLabels(config).singular}`)
        toast.error(errorMessage)
      },
    })
  }
}

/**
 * Creates an update mutation hook
 */
export function createUpdateMutation<T, TUpdate>(
  config: ResourceMutationConfig<T, any, TUpdate>,
): () => UpdateMutationResult<T, { id: string; data: TUpdate }> {
  return function useUpdateMutation() {
    const queryClient = useQueryClient()

    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: TUpdate }) =>
        config.api.update(id, data),
      onSuccess: (updatedItem, variables) => {
        // Update the item in cache
        queryClient.setQueryData(
          config.queryKeyFactory.detail(variables.id, {
            includeCustomFields: true,
          }),
          updatedItem,
        )

        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: unknown) => {
            if (!oldData) return oldData
            const updatedRecord = updatedItem as Record<string, unknown>

            if (
              oldData &&
              typeof oldData === 'object' &&
              'data' in oldData &&
              Array.isArray(oldData.data)
            ) {
              return {
                ...oldData,
                data: oldData.data.map((item: Record<string, unknown>) =>
                  item?.id === variables.id
                    ? config.replaceUpdatedRows
                      ? updatedRecord
                      : { ...item, ...updatedRecord }
                    : item,
                ),
              }
            }

            if (Array.isArray(oldData)) {
              return oldData.map((item: Record<string, unknown>) =>
                item?.id === variables.id
                  ? config.replaceUpdatedRows
                    ? updatedRecord
                    : { ...item, ...updatedRecord }
                  : item,
              )
            }

            return oldData
          },
        )

        // Mark inactive lists stale for consistency without replacing active UI state.
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
          refetchType: 'inactive',
        })

        // Custom invalidation strategy
        if (config.invalidation?.onUpdate) {
          applyInvalidationStrategy(
            queryClient,
            config.invalidation.onUpdate,
            'inactive',
          )
        }

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:updated`, {
            id: variables.id,
            [config.resourceNameSingular]: updatedItem,
            ts: Date.now(),
          })
        }
      },
      onError: error => {
        logger.error(`[useUpdate${config.resourceName}Mutation] Error:`, error)
        const errorMessage =
          config.messages?.updateError ||
          (error instanceof Error
            ? error.message
            : `Failed to update ${resolveLabels(config).singular}`)
        toast.error(errorMessage)
      },
    })
  }
}

/**
 * Creates an update field mutation hook (for single field updates)
 */
export function createUpdateFieldMutation<T>(
  config: ResourceMutationConfig<T, any, any>,
): () => UpdateMutationResult<
  T,
  { id: string; field: string; value: unknown }
> {
  return function useUpdateFieldMutation() {
    const queryClient = useQueryClient()

    if (!config.api.updateField) {
      throw new Error('updateField API method not provided in configuration')
    }

    return useMutation({
      mutationFn: ({
        id,
        field,
        value,
      }: {
        id: string
        field: string
        value: unknown
      }) => config.api.updateField!(id, field, value),
      // Optimistic update: update cache immediately before API call completes
      onMutate: async ({ id, field, value }) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries({
          queryKey: config.queryKeyFactory.lists(),
        })
        await queryClient.cancelQueries({
          queryKey: config.queryKeyFactory.detail(id, {
            includeCustomFields: true,
          }),
        })

        // Snapshot previous values for rollback
        const previousListData = queryClient.getQueriesData({
          queryKey: config.queryKeyFactory.lists(),
        })
        const previousDetailData = queryClient.getQueryData(
          config.queryKeyFactory.detail(id, { includeCustomFields: true }),
        )

        // Always apply the user's own edit immediately. The patch only touches
        // the edited row, so open editors on other rows are unaffected — deferral
        // is reserved for remote (realtime) updates, never the user's own action.
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: any) =>
            patchRowInListCache(
              oldData,
              id,
              item => ({ ...item, [field]: value }),
              config.listItemsKey,
            ),
        )

        // Optimistically update detail cache
        queryClient.setQueryData(
          config.queryKeyFactory.detail(id, { includeCustomFields: true }),
          (oldData: any) => {
            if (!oldData) return oldData
            return { ...oldData, [field]: value }
          },
        )

        // Return context for rollback on error
        return { previousListData, previousDetailData }
      },
      // On error, rollback optimistic update
      onError: (error, variables, context) => {
        // Rollback list caches
        if (context?.previousListData) {
          context.previousListData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }

        // Rollback detail cache
        if (context?.previousDetailData) {
          queryClient.setQueryData(
            config.queryKeyFactory.detail(variables.id, {
              includeCustomFields: true,
            }),
            context.previousDetailData,
          )
        }

        logger.error(
          `[useUpdateField${config.resourceName}Mutation] Error:`,
          error,
        )
        toast.error(`Failed to update ${resolveLabels(config).singular} field`)
      },
      onSuccess: (updatedItem, variables) => {
        // The user's own edit always lands immediately. The optimistic value from
        // onMutate is already showing; this swaps in the authoritative server row.
        // Update the item in detail cache
        queryClient.setQueryData(
          config.queryKeyFactory.detail(variables.id, {
            includeCustomFields: true,
          }),
          updatedItem,
        )

        // Optimistically update the item in all list caches instead of invalidating
        // This prevents refetches that might return stale data and overwrite the update
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: any) =>
            patchRowInListCache(
              oldData,
              variables.id,
              item =>
                config.replaceUpdatedRows
                  ? updatedItem
                  : { ...item, ...updatedItem },
              config.listItemsKey,
            ),
        )

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:updated`, {
            id: variables.id,
            [config.resourceNameSingular]: updatedItem,
            ts: Date.now(),
          })
        }

        // Note: No toast for field updates as they're often silent operations
        // The optimistic update already showed the change, so we just confirm with server data
      },
    })
  }
}

/**
 * Creates a delete mutation hook
 */
export function createDeleteMutation<T>(
  config: ResourceMutationConfig<T, any, any>,
): () => DeleteMutationResult<void, string> {
  return function useDeleteMutation() {
    const queryClient = useQueryClient()

    return useMutation({
      mutationFn: (id: string) => config.api.delete(id),
      onSuccess: (_, deletedId) => {
        // Remove the item from cache
        queryClient.removeQueries({
          queryKey: config.queryKeyFactory.detail(deletedId),
        })
        queryClient.removeQueries({
          predicate: query => {
            if (!Array.isArray(query.queryKey)) return false
            const rootKey = config.queryKeyFactory.all[0]
            if (query.queryKey[0] !== rootKey) return false

            const cachedItem = queryClient.getQueryData<{ id?: string }>(
              query.queryKey,
            )
            return cachedItem?.id === deletedId
          },
        })

        // Remove deleted item from all list caches immediately
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: any) => {
            if (!oldData) {
              return oldData
            }

            if (Array.isArray(oldData)) {
              return oldData.filter((item: any) => item?.id !== deletedId)
            }

            if (
              typeof oldData === 'object' &&
              'data' in oldData &&
              Array.isArray(oldData.data)
            ) {
              const nextData = oldData.data.filter(
                (item: any) => item?.id !== deletedId,
              )
              const removedCount = oldData.data.length - nextData.length
              const nextMeta =
                typeof oldData.meta?.total === 'number'
                  ? {
                      ...oldData.meta,
                      total: Math.max(0, oldData.meta.total - removedCount),
                    }
                  : oldData.meta

              return {
                ...oldData,
                data: nextData,
                ...(oldData.meta ? { meta: nextMeta } : {}),
              }
            }

            return oldData
          },
        )

        // Mark inactive lists stale. Active caches were already pruned above,
        // so an immediate active refetch can rehydrate stale deleted rows.
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
          refetchType: 'inactive',
        })

        // Custom invalidation strategy
        if (config.invalidation?.onDelete) {
          applyInvalidationStrategy(
            queryClient,
            config.invalidation.onDelete,
            'inactive',
          )
        }

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:deleted`, {
            id: deletedId,
            ts: Date.now(),
          })
        }

        toast.success(
          config.messages?.deleteSuccess ||
            `${resolveLabels(config).Singular} deleted successfully`,
        )
      },
      onError: error => {
        logger.error(`[useDelete${config.resourceName}Mutation] Error:`, error)
        const errorMessage =
          config.messages?.deleteError ||
          (error instanceof Error
            ? error.message
            : `Failed to delete ${resolveLabels(config).singular}`)
        toast.error(errorMessage)
      },
    })
  }
}

/**
 * Helper to apply cache invalidation strategy
 */
function applyInvalidationStrategy(
  queryClient: ReturnType<typeof useQueryClient>,
  strategy: CacheInvalidationStrategy,
  refetchType?: 'active' | 'inactive' | 'all' | 'none',
) {
  if (strategy.invalidateLists) {
    // Handled by default - included for completeness
  }

  if (strategy.invalidateDetail) {
    queryClient.invalidateQueries({
      queryKey: [strategy.invalidateDetail],
      ...(refetchType ? { refetchType } : {}),
    })
  }

  if (strategy.invalidateRelated) {
    strategy.invalidateRelated.forEach(queryKey => {
      queryClient.invalidateQueries({
        queryKey,
        ...(refetchType ? { refetchType } : {}),
      })
    })
  }

  if (strategy.removeQuery) {
    queryClient.removeQueries({ queryKey: strategy.removeQuery })
  }

  // Handle additional invalidations (functions that return query keys)
  if (strategy.additionalInvalidations) {
    strategy.additionalInvalidations.forEach(getQueryKey => {
      const queryKey = getQueryKey()
      queryClient.invalidateQueries({
        queryKey: queryKey as readonly string[],
        ...(refetchType ? { refetchType } : {}),
      })
    })
  }
}

/**
 * Creates a complete set of mutation hooks for a resource
 */
export function createResourceMutations<T, TCreate, TUpdate>(
  config: ResourceMutationConfig<T, TCreate, TUpdate>,
) {
  return {
    useCreateMutation: createCreateMutation<T, TCreate>(config),
    useUpdateMutation: createUpdateMutation<T, TUpdate>(config),
    useDeleteMutation: createDeleteMutation<T>(config),
    ...(config.api.updateField
      ? { useUpdateFieldMutation: createUpdateFieldMutation<T>(config) }
      : {}),
  }
}
